import React from 'react';
import Icon from '@amzn/awsui-components-console/icon';

import { useTranslation } from 'src/i18n/shim/useTranslation';
import { buildPaginationWindow, ELLIPSIS } from './paginationWindow';
import styles from './CompactPagination.module.scss';

export interface CompactPaginationProps {
  /** 1-based index of the current page. */
  currentPageIndex: number;
  /** Total pages known so far. */
  pagesCount: number;
  /**
   * Whether more pages can still be loaded beyond {@link pagesCount}.
   *
   * When true the next arrow stays enabled on the last known page, so the
   * consumer can extend the range on demand in response to the click.
   */
  hasMore?: boolean;
  /** Disables every control (e.g. while a page is loading). */
  disabled?: boolean;
  onPageChange: (pageIndex: number) => void;
}

/**
 * Pager that renders at most 5 page numbers in total (see `VISIBLE_PAGE_COUNT`
 * in `./paginationWindow`), e.g. `< 1 … 5 6 7 8 >`.
 *
 * Cloudscape's `Pagination` hardcodes a 7-control window with no prop to
 * configure it, which overflows the narrow namespace sidebar. This component
 * reimplements the minimum needed — prev/next arrows, a pinned first page, and
 * a sliding window — preserving Cloudscape's semantics: `<nav>` + list markup,
 * `aria-current="page"` on the active page, per-control aria labels, and arrows
 * disabled at the range ends.
 *
 * For tables with a known total page count, prefer `PaginationControl` (the
 * Cloudscape wrapper) rather than this.
 */
const CompactPagination: React.FC<CompactPaginationProps> = ({
  currentPageIndex,
  pagesCount,
  hasMore = false,
  disabled = false,
  onPageChange,
}) => {
  const { t } = useTranslation();
  const slots = buildPaginationWindow(currentPageIndex, pagesCount);
  const isFirst = currentPageIndex <= 1;
  // The last KNOWN page is only the last page when the source is exhausted.
  // With `hasMore` the arrow stays live so the click can pull the next batch.
  const isLast = currentPageIndex >= pagesCount && !hasMore;

  return (
    <nav
      aria-label={t('pagination-namespace-nav-aria')}
      className={styles.root}
    >
      <ul className={styles.list}>
        <li>
          <button
            type="button"
            className={styles.arrow}
            aria-label={t('pagination-previous')}
            disabled={disabled || isFirst}
            onClick={() => onPageChange(currentPageIndex - 1)}
          >
            <Icon
              name="angle-left"
              variant={disabled || isFirst ? 'disabled' : 'normal'}
            />
          </button>
        </li>

        {slots.map((slot, index) =>
          slot === ELLIPSIS ? (
            // Decorative: the gap conveys no information to assistive tech
            // beyond what the surrounding page numbers already do.
            <li
              // eslint-disable-next-line react/no-array-index-key
              key={`ellipsis-${index}`}
              className={styles.dots}
              aria-hidden="true"
            >
              …
            </li>
          ) : (
            <li key={slot}>
              <button
                type="button"
                className={
                  slot === currentPageIndex
                    ? `${styles.pageNumber} ${styles.pageNumberCurrent}`
                    : styles.pageNumber
                }
                aria-label={t('pagination-page', { pageNumber: slot })}
                aria-current={slot === currentPageIndex ? 'page' : undefined}
                disabled={disabled}
                onClick={() => onPageChange(slot)}
              >
                {slot}
              </button>
            </li>
          ),
        )}

        <li>
          <button
            type="button"
            className={styles.arrow}
            aria-label={t('pagination-next')}
            disabled={disabled || isLast}
            onClick={() => onPageChange(currentPageIndex + 1)}
          >
            <Icon
              name="angle-right"
              variant={disabled || isLast ? 'disabled' : 'normal'}
            />
          </button>
        </li>
      </ul>
    </nav>
  );
};

export default CompactPagination;
